/**
 * 4B-A.1 assisted PD prototype (standalone, R&D): the rules the simple three-screen capture needs on top of the frozen measurement — when the subject is placed well enough to start, how the five-second window is split for an internal consistency check, and what the screen is allowed to show afterwards.
 *
 * Nothing here changes how a frame is measured or how a capture is aggregated: every frame still goes through `analyseCardlessFrame` and `CardlessCapture` exactly as `cardless-iris-ratio 0.1` does. What this adds is a fixed-length window instead of the experiment's 30-good-frame target, and one extra refusal: a prototype consistency gate derived from the existing maxHalvesDiffMm = 1 mm, applied to the early, middle and late thirds of the window. Only the number is reused; using it across three temporal sub-windows is new prototype behaviour, neither clinically validated nor previously evidence-backed. Both additions are why the page reports a separate algorithm version.
 */
import { CAPTURE_SETTINGS } from './capture';
import type { CaptureResult } from './capture';
import type { CardlessOutcome, CardlessRejection } from './frame';

/** The measurement behaviour this prototype runs: the frozen per-frame maths and aggregation, a fixed window, and the sub-window check. Never call this 0.1. */
export const ASSISTED_ALGORITHM = 'cardless-assisted-pd 0.2-prototype';
/** The capture experience, versioned apart from the measurement. */
export const ASSISTED_EXPERIENCE = 'assisted-pd-v1';

export const ASSISTED = {
  /** The measurement window, from the first acquired frame. */
  windowMs: 5000,
  /** 3 · 2 · 1 before it starts. */
  countdownMs: 3000,
  /** The subject must look ready in this many of the last frames before the countdown starts. */
  armWindow: 6,
  armNeeded: 5,
  /** A countdown is abandoned when this many of the last frames are refused again. */
  disarmNeeded: 3,
  /** Equal thirds of the window: early, middle, late. */
  subWindows: 3,
  /** A third with fewer accepted frames than this has no median worth comparing, so the capture is not shown. */
  minPerSubWindow: 3,
} as const;

/** Prototype consistency gate derived from the existing `maxHalvesDiffMm` = 1 mm. Only the number is reused: the frozen measurement applies it between a capture's two halves, and applying it across three temporal sub-windows is new prototype behaviour — not clinically validated, and not previously evidence-backed. */
export const SUB_WINDOW_TOLERANCE_MM = CAPTURE_SETTINGS.stability.maxHalvesDiffMm;

export type Guidance =
  | 'HOLD_STILL'
  | 'CENTRE_FACE'
  | 'MOVE_CLOSER'
  | 'MOVE_BACK'
  | 'HEAD_STRAIGHT'
  | 'OPEN_EYES'
  | 'MORE_LIGHT'
  | 'REMOVE_GLASSES'
  | 'ONE_FACE'
  | 'LOOK_AT_SCREEN';

/** One plain instruction per refusal: the product screen never shows a reason code or a number. */
const GUIDANCE: Record<CardlessRejection, Guidance> = {
  NO_FACE: 'LOOK_AT_SCREEN',
  MULTIPLE_FACES: 'ONE_FACE',
  BLINK: 'HOLD_STILL',
  EYES_CLOSED: 'OPEN_EYES',
  GLASSES_OR_REFLECTION: 'REMOVE_GLASSES',
  EXCESSIVE_YAW: 'HEAD_STRAIGHT',
  EXCESSIVE_PITCH: 'HEAD_STRAIGHT',
  EXCESSIVE_ROLL: 'HEAD_STRAIGHT',
  BLUR: 'HOLD_STILL',
  LOW_LIGHT: 'MORE_LIGHT',
  IRIS_TOO_SMALL: 'MOVE_CLOSER',
  TOO_CLOSE: 'MOVE_BACK',
  IRIS_FIT_FAILED: 'LOOK_AT_SCREEN',
  LEFT_RIGHT_IRIS_DISAGREEMENT: 'LOOK_AT_SCREEN',
  LANDMARK_INSTABILITY: 'HOLD_STILL',
  PD_OUTLIER: 'HOLD_STILL',
};

/** What to tell the subject from the last few frames: hold still while they are good, otherwise the commonest thing in the way. */
export function guidanceFrom(recent: readonly CardlessOutcome[]): Guidance {
  const last = recent.slice(-ASSISTED.armWindow);
  if (!last.length) return 'CENTRE_FACE';
  if (last.filter((o) => o.ok).length >= ASSISTED.armNeeded) return 'HOLD_STILL';
  const counts = new Map<Guidance, number>();
  for (const o of last)
    if (!o.ok) {
      const g = GUIDANCE[o.reason];
      counts.set(g, (counts.get(g) ?? 0) + 1);
    }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'HOLD_STILL';
}

/** Whether the last frames look good enough to start the countdown. */
export const armed = (recent: readonly CardlessOutcome[]): boolean =>
  recent.slice(-ASSISTED.armWindow).filter((o) => o.ok).length >= ASSISTED.armNeeded;

/** Whether a running countdown should be abandoned: the subject has moved out of position again. */
export const disarmed = (recent: readonly CardlessOutcome[]): boolean =>
  recent.slice(-ASSISTED.armWindow).filter((o) => !o.ok).length >= ASSISTED.disarmNeeded;

export interface SubWindow {
  /** 1, 2, 3 — early, middle, late. */
  index: number;
  frames: number;
  medianPdMm: number | null;
}

export interface SubWindowCheck {
  windows: SubWindow[];
  /** Largest difference between two sub-window medians, mm. */
  spreadMm: number | null;
  toleranceMm: number;
  /** True only when every third had enough frames and they agree within the tolerance. */
  consistent: boolean;
  reason: 'OK' | 'TOO_FEW_FRAMES' | 'SUB_WINDOW_DRIFT';
}

const median = (xs: readonly number[]): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const h = Math.floor(s.length / 2);
  return s.length % 2 ? s[h]! : (s[h - 1]! + s[h]!) / 2;
};

/**
 * Splits the accepted frames of one window into equal thirds by time and compares their medians.
 *
 * It runs on every frame the gates accepted during the window, not on the subset the aggregation kept, so a drift that the outlier cut would hide still shows here. The thirds are by clock time, not by count, so a pause in accepted frames leaves a thin third rather than a silently stretched one.
 */
export function subWindowCheck(
  frames: readonly { t: number; pdMm: number }[],
  startedAt: number,
  windowMs: number = ASSISTED.windowMs,
  toleranceMm: number = SUB_WINDOW_TOLERANCE_MM,
): SubWindowCheck {
  const each = windowMs / ASSISTED.subWindows;
  const windows: SubWindow[] = Array.from({ length: ASSISTED.subWindows }, (_, i) => {
    const from = startedAt + i * each;
    const to = i === ASSISTED.subWindows - 1 ? Infinity : startedAt + (i + 1) * each;
    const pds = frames.filter((f) => f.t >= from && f.t < to).map((f) => f.pdMm);
    return { index: i + 1, frames: pds.length, medianPdMm: median(pds) };
  });
  const medians = windows.map((w) => w.medianPdMm);
  const thin = windows.some((w) => w.frames < ASSISTED.minPerSubWindow);
  const spreadMm =
    medians.every((m): m is number => m !== null) && medians.length
      ? Math.max(...medians) - Math.min(...medians)
      : null;
  const reason: SubWindowCheck['reason'] = thin
    ? 'TOO_FEW_FRAMES'
    : spreadMm !== null && spreadMm > toleranceMm
      ? 'SUB_WINDOW_DRIFT'
      : 'OK';
  return { windows, spreadMm, toleranceMm, consistent: reason === 'OK', reason };
}

export type AssistedState = 'RESULT' | 'RETRY';
export interface AssistedDecision {
  state: AssistedState;
  /** Why a retry is asked for; never shown as a code. */
  reason: 'OK' | 'NOT_STABLE' | 'TOO_FEW_GOOD_FRAMES' | 'SUB_WINDOW_DRIFT' | 'NO_RESULT';
  /** The number the screen may show, or null when it may show none. */
  totalPdMm: string | null;
  quality: CaptureResult['captureQuality'];
}

/** What the result screen may show: the frozen capture's own verdict first, then the prototype's sub-window check. A refused capture never shows a number. */
export function assistedDecision(result: CaptureResult, check: SubWindowCheck): AssistedDecision {
  const quality = result.captureQuality;
  if (result.status === 'TIMED_OUT' || result.status === 'CANCELLED')
    return { state: 'RETRY', reason: 'TOO_FEW_GOOD_FRAMES', totalPdMm: null, quality };
  if (result.status === 'UNSTABLE')
    return { state: 'RETRY', reason: 'NOT_STABLE', totalPdMm: null, quality };
  if (!check.consistent)
    return {
      state: 'RETRY',
      reason: check.reason === 'TOO_FEW_FRAMES' ? 'TOO_FEW_GOOD_FRAMES' : 'SUB_WINDOW_DRIFT',
      totalPdMm: null,
      quality,
    };
  if (result.totalPdMm === null)
    return { state: 'RETRY', reason: 'NO_RESULT', totalPdMm: null, quality };
  return { state: 'RESULT', reason: 'OK', totalPdMm: result.totalPdMm, quality };
}
